export interface SparklineProps {
  /** Prices oldest → newest. */
  data: number[];
  width?: number;
  height?: number;
  /** Defaults to the up/down semantic derived from first vs last point. */
  color?: string;
  strokeWidth?: number;
}

/**
 * A tiny inline trend line. Renders an empty SVG of the same size when there
 * is nothing to draw, so the row keeps its geometry while bars load.
 */
export const Sparkline = ({
  data,
  width = 72,
  height = 24,
  color,
  strokeWidth = 1.5,
}: SparklineProps) => {
  if (data.length < 2) {
    return <svg width={width} height={height} role="none" aria-hidden="true" />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = width / (data.length - 1);
  const usableHeight = height - strokeWidth * 2;

  const path = data
    .map((value, index) => {
      const x = index * stepX;
      const y =
        strokeWidth + usableHeight - ((value - min) / span) * usableHeight;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  const stroke =
    color ??
    (data[data.length - 1] >= data[0] ? "var(--green-9)" : "var(--red-9)");

  return (
    // Decorative: the price and percentage carry the same information as text.
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="none"
      aria-hidden="true"
    >
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
